import type { InputProps } from "../../../../types";
import useGoogle from "react-google-autocomplete/lib/usePlacesAutocompleteService";

import "../../../../styles/input.css";
import "./style.css";

import { useState, useRef } from "react";
import { classNameParser } from "../../../../helpers";

export type AddressAutocompleteDetailsType = {
  streetNumber?: string;
  route?: string;
  locality?: string;
  country?: string;
  stateCode?: string;
  postalCode?: string;
};

type AddressAutocompleteInputProps = InputProps<
  string,
  {
    apiKey: string;
    label?: string;
    onChangeAddressDetails?: (addressDetails: AddressAutocompleteDetailsType) => void;
  }
>;

export const AddressAutocomplete = (props: AddressAutocompleteInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const { placePredictions, getPlacePredictions, placesService } = useGoogle({
    apiKey: props.apiKey,
    libraries: ["places"],

    options: {
      fields: ["geometry"],
      componentRestrictions: { country: "ca" },
    },
  } as any);

  const [value, setValue] = useState<string>(props.defaultValue ?? "");
  const [showOptions, setShowOptions] = useState(false);

  const formatAddressDetails = (
    addressComponents: { long_name?: string; short_name?: string; types: string[] }[]
  ): AddressAutocompleteDetailsType => {
    const streetNumber = addressComponents.find((tmp) =>
      tmp.types.find((type) => type === "street_number")
    )?.short_name;
    const route = addressComponents.find((tmp) => tmp.types.find((type) => type === "route"))?.short_name;
    const locality = addressComponents.find((tmp) => tmp.types.find((type) => type === "locality"))?.short_name ?? addressComponents.find((tmp) => tmp.types.find((type) => type === "sublocality"))?.short_name;
    const country = addressComponents.find((tmp) => tmp.types.find((type) => type === "country"))?.short_name;
    const stateCode = addressComponents.find((tmp) =>
      tmp.types.find((type) => type === "administrative_area_level_1")
    )?.short_name;
    const postalCode = addressComponents.find((tmp) => tmp.types.find((type) => type === "postal_code"))?.short_name;

    return {
      streetNumber,
      route,
      locality,
      country,
      stateCode,
      postalCode,
    };
  };

  const onClickItem = async (item: any) => {
    setValue(item.description);

    placesService?.getDetails(
      {
        placeId: item.place_id,
        fields: ["name", "formatted_address", "place_id", "geometry", "address_components"],
      },
      (result: any) => {
        setValue(result.formatted_address ?? item.description);
        const addressDetails = formatAddressDetails(result.address_components ?? []);
        if (props.onChangeAddressDetails) {
          props.onChangeAddressDetails(addressDetails);
        }
      }
    );

    props.onChange && props.onChange(item.description);
    inputRef.current?.blur();
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    switch (e.key) {
      case "Enter":
        inputRef.current?.blur();
        break;
      case "Escape":
        inputRef.current?.blur();
        break;
      default:
        break;
    }
  };

  return (
    <>
      <div
        id={props.id}
        key={props.key}
        className={classNameParser(
          "global--input-container input-address-autocomplete--text-container",
          props.className
        )}
        style={props.style}
      >
        <input
          ref={inputRef}
          value={value}
          className={`global--input ${value ? "global--with-value" : ""} ${!props.autoComplete ? "hide-autocomplete" : ""}`}
          onKeyUp={handleKeyPress}
          onBlur={(evt) => {
            evt.preventDefault();
            setShowOptions(false);
          }}
          onFocus={(evt) => {
            evt.preventDefault();
            setShowOptions(true);
          }}
          onChange={(evt) => {
            setShowOptions(true);
            getPlacePredictions({ input: evt.target.value });
            setValue(evt.target.value);
            props.onChange && props.onChange(evt.target.value);
          }}
        />
        {placePredictions.length > 0 && showOptions && (
          <div className="input-address-autocomplete--options-box">
            <ul className="input-address-autocomplete--select-input-options">
              {placePredictions.map((item) => {
                return (
                  <li
                    key={item.place_id}
                    className="input-address-autocomplete--select-input-option"
                    onMouseDown={(evt) => {
                      evt.preventDefault();
                      onClickItem(item);
                    }}
                  >
                    {item.description}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
        {props.label && (
          <label htmlFor={props.id} className="input-address-autocomplete--text-input-label" title={props.label}>
            <span className="input-address-autocomplete--text-input-label-text">{props.label}</span>
          </label>
        )}
        {props.error && <p className="global--input-error">{props.error}</p>}
      </div>
    </>
  );
};
